import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import IconsBar from "../components/IconsBar/IconsBar";
import styled from "styled-components";
import { Button, Tabs, Tab, Box, Typography, TextField, CircularProgress } from "@mui/material";
import {
  Create as CreateIcon,
  Send as SendIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { sendGraphDiary } from "../api/autenticoAPI";
import { MultipleClassroomSelect } from "../components/MultipleClassroomSelect";
import { useNavigate } from "react-router-dom";
import { getAllClassrooms } from "../api/sponteAPI";
import { anoLetivo } from "../utils/YEARCONSTS";
import AgendaForm from "../components/AgendaForm/AgendaForm";
import { useNotification } from "../contexts/NotificationContext";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`agenda-tabpanel-${index}`}
    aria-labelledby={`agenda-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

const GraphDiary = () => {
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [classrooms, setClassrooms] = useState([]);
  const [allClassrooms, setAllClassrooms] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useNotification();

  useEffect(() => {
    getAllClassrooms(anoLetivo).then((res) => {
      setAllClassrooms(res);
    });
  }, []);

  const sendFileGraphDiary = async () => {
    if (!selectedDate) {
      showWarning("Por favor, selecione uma data para a agenda.", "Data não selecionada");
      return;
    }

    if (!classrooms || classrooms.length === 0) {
      showWarning("Por favor, selecione pelo menos uma turma.", "Turma não selecionada");
      return;
    }

    setIsSending(true);

    try {
      // Ajusta a data para meio-dia do dia selecionado para evitar problemas de fuso horário
      const [year, month, day] = selectedDate.split("-");
      const formattedDate = `${year}-${month}-${day} 12:00:00`;

      // Obter nomes das turmas selecionadas (manter ordem e correspondência com IDs)
      const classroomNames = classrooms.map((classroomId) => {
        // Garantir comparação correta de tipos (pode ser string ou number)
        const classroom = allClassrooms.find((c) => 
          String(c.TurmaID) === String(classroomId) || 
          Number(c.TurmaID) === Number(classroomId)
        );
        // Retornar nome real ou usar ID como fallback, mas manter a ordem
        if (classroom && classroom.Nome) {
          // Garantir que Nome é uma string antes de chamar trim
          const nome = String(classroom.Nome).trim();
          if (nome !== '') {
            return nome;
          }
        }
        return `Turma ${classroomId}`;
      });

      // Criar FormData com múltiplas turmas
      const fData = new FormData();
      fData.append("file", selectedImage);
      fData.append("type", "FILE");
      fData.append("date", formattedDate);
      fData.append("classroom_ids", JSON.stringify(classrooms));
      fData.append("classroom_names", JSON.stringify(classroomNames));
      fData.append("body", classroomNames.join(", "));

      const result = await sendGraphDiary(fData, "FILE");
      
      // Verificar se realmente foi criado
      if (result && result.count > 0) {
        const message = result.partialSuccess 
          ? `Agenda enviada com sucesso para ${result.count} turma(s). ${result.warnings?.length || 0} turma(s) tiveram problemas.`
          : `Agenda enviada com sucesso para ${result.count} turma(s)!`;
        
        showSuccess(message, "Agenda enviada");
        
        // Navegar para a página de estatísticas na aba de AGENDAS (índice 1)
        navigate("/autentico/stats", { state: { tabIndex: 1 } });
      } else {
        showError("Nenhuma agenda foi criada. Por favor, tente novamente.", "Erro ao enviar");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || "Erro desconhecido";
      showError(`Erro ao enviar agenda: ${errorMessage}. Por favor, tente novamente.`, "Erro ao enviar");
    } finally {
      setIsSending(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files?.[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleGeneratedImage = (imageFile, date) => {
    setSelectedImage(imageFile);
    setSelectedDate(date);
    setActiveTab(1);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Container>
      <Header />
      <IconsBar />
      <ContentArea>
        <h3 className="page-title">Agenda Diária Modo Gráfico</h3>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab
              icon={<CreateIcon />}
              iconPosition="start"
              label="Criar Agenda"
            />
            <Tab
              icon={<SendIcon />}
              iconPosition="start"
              label="Enviar Agenda"
            />
          </Tabs>
        </Box>
        <TabPanel value={activeTab} index={0}>
          <AgendaForm onImageGenerate={handleGeneratedImage} />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <SenderContainer>
            {selectedImage && (
              <img
                alt="Preview da Agenda"
                className="img-preview"
                src={URL.createObjectURL(selectedImage)}
              />
            )}

            <DateContainer>
              <TextField
                type="date"
                label="Data da Agenda"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />
            </DateContainer>

            <MultipleClassroomSelect
              classrooms={classrooms}
              setClassrooms={setClassrooms}
            />

            <FormItens encType="multipart/form-data">
              <div className="file-send">
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  {selectedImage
                    ? "Imagem atual:"
                    : "Selecione uma imagem ou gere uma na aba anterior"}
                </Typography>

                <FileUploadBox onClick={handleUploadClick}>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                  />
                  <UploadIcon sx={{ mr: 1 }} />
                  <span>
                    {selectedImage
                      ? selectedImage.name
                      : "Clique para selecionar um arquivo"}
                  </span>
                </FileUploadBox>

                <Button
                  onClick={sendFileGraphDiary}
                  variant="contained"
                  disabled={!selectedImage || !selectedDate || isSending}
                  startIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                >
                  {isSending ? "Enviando..." : "Enviar Agenda"}
                </Button>
              </div>
            </FormItens>
          </SenderContainer>
        </TabPanel>
      </ContentArea>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #1e958c 0%, #0b4f6c 40%, #f5f7fb 100%);
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 16px auto 24px;
  padding: 20px 16px 28px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  display: flex;
  flex-direction: column;
  gap: 18px;
  backdrop-filter: blur(8px);
  
  .page-title {
    text-align: center;
    font-weight: 700;
    font-size: 22px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #1e958c;
    margin: 4px auto 8px;
  }
  .img-preview {
    height: 200px;
    margin-bottom: 15px;
    width: 100%;
    object-fit: contain;
    border-radius: 12px;
  }
`;

const DateContainer = styled.div`
  width: 80%;
  margin: 0 auto 15px auto;

  .MuiTextField-root {
    background-color: white;
    border-radius: 4px;
  }
`;

const SenderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormItens = styled.form`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  padding: 15px;
  min-height: 180px;
  .file-send {
    width: 80%;
    margin: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 12px;
  }
`;

const FileUploadBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px dashed #999;
  border-radius: 4px;
  width: 100%;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    border-color: #666;
  }

  span {
    color: #666;
  }
`;

export default GraphDiary;
